/* eslint-disable react-hooks/exhaustive-deps */

import { FC, useContext, useMemo } from 'react';
import {
    SearchContext,
    SeoLinks,
    Row,
    SeoLink
} from '@zoopla/listing-search-context';
import { Heading4, Divider } from '@blueprint/ui';
import triggerTracking from '@blueprint/tracking';
import {
    SeoLinksWrapper,
    TownsLinkText,
    CurrentlyAvailableLinkText,
    SeoLinksSection,
    SeoLinksInnerWrapper
} from './SeoLinks.styles';

const bedroomsRegExp = /\d-bedrooms?/;

const titleText = (type: string, geo: string): string => {
    switch (type) {
        case 'sale':
            return `Currently available for sale in ${geo}`;
        case 'rent':
            return `Currently available to rent in ${geo}`;
        default:
            return '';
    }
};

const getSEOHeading = (currentlyAvailable: SeoLinks[]) => {
    const newHomesSEOHeading = `Currently available for sale in ${currentlyAvailable[0]?.geo}`;

    const searchResultsPageSEOHeading = titleText(
        currentlyAvailable[0]?.transactionType,
        currentlyAvailable[0]?.geo
    );

    return currentlyAvailable[0]?.section === 'new-homes'
        ? newHomesSEOHeading
        : searchResultsPageSEOHeading;
};

const formatSection = (section: string) => {
    return section.replace('new-homes', 'for-sale').replace('-', ' ');
};

const formatBedrooms = (uri: string) => {
    const bedrooms = uri.match(bedroomsRegExp);

    if (bedrooms) {
        return bedrooms[0].replace('-', ' ');
    }

    return 'All';
};

const formatLinkText = (
    uri: string,
    section: string,
    propertyType: string,
    geo: string
) => {
    let linkText;

    const isNewProperty = section.match('new-homes');
    const hasBedrooms = uri.match(bedroomsRegExp);

    const formattedBedrooms = formatBedrooms(uri);
    const formattedSection = formatSection(section);

    if (isNewProperty && !hasBedrooms) {
        return `${formattedBedrooms} new ${propertyType} ${formattedSection} in ${geo}`;
    }

    linkText = `${formattedBedrooms} ${propertyType} ${formattedSection}`;

    if (isNewProperty) {
        linkText = `New ${linkText}`;
    }

    if (!hasBedrooms) {
        linkText = `${linkText} in ${geo}`;
    }

    return linkText;
};

export const SeoLinksComponent: FC<React.PropsWithChildren<unknown>> = () => {
    const { state } = useContext(SearchContext);

    const seoLinksData = state.seoAccordions;

    const currentlyAvailable = seoLinksData.filter(
        (accordion: SeoLinks) => accordion.name === 'currentlyAvailable'
    );

    const townsAndCities = seoLinksData.filter(
        (accordion: SeoLinks) => accordion.name === 'townsAndCities'
    );
    const townsAndCitiesObj = townsAndCities[0];
    const currentlyAvailableObj = currentlyAvailable[0];

    const formatCurrentlyAvailable = (propertyType: string) => {
        const typeLinks = currentlyAvailableObj?.rows?.map((row: Row) => {
            return (
                row.links &&
                row.links.filter(
                    (link: SeoLink) => link.propertyType === propertyType
                )
            );
        });

        return (
            <>
                <Divider variant="horizontal" />
                <SeoLinksInnerWrapper>
                    {typeLinks &&
                        typeLinks.map((links: SeoLink[]) => {
                            return (
                                links &&
                                links.map(
                                    (
                                        {
                                            section,
                                            uri,
                                            propertyType,
                                            geo
                                        }: SeoLink,
                                        index: number
                                    ) => {
                                        const formattedLinkText = `${formatLinkText(
                                            uri,
                                            section,
                                            propertyType,
                                            geo
                                        )}`;

                                        return (
                                            <CurrentlyAvailableLinkText
                                                href={uri}
                                                key={geo + index}
                                                variant="tertiary"
                                                data-testid="seo-currently-available-links"
                                                onClick={() =>
                                                    triggerTracking({
                                                        event: 'GAEvent',
                                                        eventCategory:
                                                            'search results',
                                                        eventAction:
                                                            'SEO widget click',
                                                        eventLabel:
                                                            formattedLinkText
                                                    })
                                                }
                                                tabIndex={0}
                                            >
                                                {formattedLinkText}
                                            </CurrentlyAvailableLinkText>
                                        );
                                    }
                                )
                            );
                        })}
                </SeoLinksInnerWrapper>
            </>
        );
    };

    const formatTownAndCities = () => (
        <>
            <Divider variant="horizontal" />
            <SeoLinksInnerWrapper>
                {townsAndCitiesObj?.links?.map(
                    (link: SeoLink, index: number) => {
                        return (
                            <TownsLinkText
                                href={link.uri}
                                key={link.geo + index}
                                variant="tertiary"
                                data-testid="seo-town-links"
                                onClick={() =>
                                    triggerTracking({
                                        event: 'GAEvent',
                                        eventCategory: 'search results',
                                        eventAction: 'SEO widget click',
                                        eventLabel: link.geo
                                    })
                                }
                                tabIndex={0}
                            >
                                {link.geo}
                            </TownsLinkText>
                        );
                    }
                )}
            </SeoLinksInnerWrapper>
        </>
    );
    const seoHeading = getSEOHeading(currentlyAvailable);
    return useMemo(
        () => (
            <SeoLinksWrapper data-testid="seo-links">
                {currentlyAvailable.length > 0 && (
                    <SeoLinksSection>
                        <SeoLinksInnerWrapper>
                            <Heading4 size="8" color={'text-title'}>
                                {seoHeading}
                            </Heading4>
                        </SeoLinksInnerWrapper>

                        {formatCurrentlyAvailable('houses')}
                        {formatCurrentlyAvailable('flats')}
                        {formatCurrentlyAvailable('property')}
                        {formatCurrentlyAvailable('bungalows')}
                        {formatCurrentlyAvailable('studios')}
                    </SeoLinksSection>
                )}

                {townsAndCities.length > 0 && (
                    <SeoLinksSection>
                        <SeoLinksInnerWrapper>
                            <Heading4 size="8" color={'text-title'}>
                                Towns and cities within {townsAndCities[0]?.geo}
                            </Heading4>
                        </SeoLinksInnerWrapper>

                        {formatTownAndCities()}
                    </SeoLinksSection>
                )}
            </SeoLinksWrapper>
        ),
        [seoLinksData]
    );
};
